import { TourConfig, startTour, CONTINUE_PLATFORM_TOUR_BUTTON } from './TourSystem';
import PageWelcomeModal from './PageWelcomeModal';
import 'driver.js/dist/driver.css';

// PHASE 1: Overview Tab (default)
const clientPortalPhase1Config: TourConfig = {
    tourId: 'client-portal-phase1',
    steps: [
        {
            popover: {
                title: '🏢 Welcome to Client Portal',
                description: 'Your executive dashboard for managing all projects, budgets, and communications. This tour covers all four tabs: Overview, Projects, Financials, and Activity. Let me show you everything in detail.',
            }
        },
        {
            element: '#client-stats',
            popover: {
                title: '📊 Performance Metrics',
                description: 'These three critical metrics give you instant visibility: (1) Active Projects - currently running work, (2) Total Budget - combined spending across all projects, (3) Pending Deliverables - awaiting your approval or freelancer submission. These update in real-time.',
                side: "bottom",
            }
        },
        {
            element: '#client-tabs',
            popover: {
                title: '🗂️ Four Navigation Sections',
                description: 'Client Portal has four main tabs: Overview (high-level summary), Projects (detailed project management), Financials (budget tracking and invoices), Activity (team communications). I will walk you through each one.',
                side: "bottom",
            }
        },
        {
            element: '#client-content',
            popover: {
                title: '📈 Overview Dashboard (Current View)',
                description: 'You are viewing the Overview tab, which shows: your most active projects with progress bars, recent financial transactions, upcoming deadlines, and team activity. This is your command center for quick status checks.',
                side: "top",
            }
        },
        {
            popover: {
                title: '🔄 Switching to Projects Tab',
                description: 'Now I will show you the Projects view where you can drill down into each project, view milestones, approve deliverables, and communicate with freelancers. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const projectsTab = document.querySelector('#client-tabs button:nth-child(2)');
            if (projectsTab) {
                (projectsTab as HTMLElement).click();
                setTimeout(() => {
                    startClientPortalPhase2Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 2: Projects Tab
const clientPortalPhase2Config: TourConfig = {
    tourId: 'client-portal-phase2',
    steps: [
        {
            element: '#client-content',
            popover: {
                title: '📁 Projects Management View',
                description: 'This tab displays all your projects in detail. Each project card shows: freelancer name, project title, current phase/milestone, completion percentage, budget allocated vs spent, deadline countdown, and action buttons. Click any project to open its dedicated workspace.',
                side: "top",
            }
        },
        {
            popover: {
                title: '⚙️ Project Actions Available',
                description: 'For each project you can: (1) View detailed milestones and deliverables, (2) Approve completed work, (3) Request revisions, (4) Release milestone payments, (5) Rate freelancer performance, (6) Download project files, (7) Close or extend projects.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to Financials Tab',
                description: 'Next, I will show you the Financials section where you track spending, view invoices, and manage payment methods. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const financialsTab = document.querySelector('#client-tabs button:nth-child(3)');
            if (financialsTab) {
                (financialsTab as HTMLElement).click();
                setTimeout(() => {
                    startClientPortalPhase3Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 3: Financials Tab
const clientPortalPhase3Config: TourConfig = {
    tourId: 'client-portal-phase3',
    steps: [
        {
            element: '#client-content',
            popover: {
                title: '💰 Financial Control Center',
                description: 'The Financials tab shows: complete transaction history, pending invoices requiring payment, payment methods on file, budget breakdown by project, tax documents for compliance, and spending analytics with charts/graphs.',
                side: "top",
            }
        },
        {
            popover: {
                title: '💳 Payment Management',
                description: 'You can: (1) Add or remove payment methods (cards, bank accounts, crypto wallets), (2) Pay outstanding invoices with one click, (3) Download receipts and invoices for accounting, (4) Set up auto-payments for trusted freelancers, (5) View detailed cost breakdowns per project.',
            }
        },
        {
            popover: {
                title: '🔄 Switching to Activity Tab',
                description: 'Finally, let me show you the Activity section for communicating with your entire team. Switching in 2 seconds...',
            }
        }
    ],
    onComplete: () => {
        setTimeout(() => {
            const messagesTab = document.querySelector('#client-tabs button:nth-child(4)');
            if (messagesTab) {
                (messagesTab as HTMLElement).click();
                setTimeout(() => {
                    startClientPortalPhase4Tour();
                }, 800);
            }
        }, 1000);
    }
};

// PHASE 4: Activity Tab
const clientPortalPhase4Config: TourConfig = {
    tourId: 'client-portal-phase4',
    steps: [
        {
            element: '#client-content',
            popover: {
                title: '💬 Unified Communications Hub',
                description: 'The Activity tab consolidates all conversations with freelancers, project updates, system notifications, and team announcements. Each conversation thread is linked to its specific project for easy context switching.',
                side: "top",
            }
        },
        {
            popover: {
                title: '📧 Communication Features',
                description: 'You can: (1) Send messages to individual freelancers or entire project teams, (2) Attach files and deliverables to messages, (3) Mark conversations as read/unread, (4) Search message history, (5) Receive email notifications for new messages, (6) Enable/disable notifications per project.',
            }
        },
        {
            popover: {
                title: '💡 Portal Pro Tips',
                description: '(1) Check Overview tab daily for status updates. (2) Use Projects tab for detailed work review. (3) Financials tab is essential for budget control. (4) Activity tab ensures clear communication. (5) All tabs sync in real-time across devices.',
            }
        },
        {
            popover: {
                title: '🎯 Complete Client Control',
                description: `You now know all four sections of the Client Portal. This is your executive dashboard for managing every aspect of your outsourced work - from project oversight to budget management to team communications.${CONTINUE_PLATFORM_TOUR_BUTTON}`,
            }
        }
    ],
};

export const startClientPortalTour = () => {
    startTour(clientPortalPhase1Config);
};

const startClientPortalPhase2Tour = () => {
    startTour(clientPortalPhase2Config);
};

const startClientPortalPhase3Tour = () => {
    startTour(clientPortalPhase3Config);
};

const startClientPortalPhase4Tour = () => {
    startTour(clientPortalPhase4Config);
};

const ClientPortalTour = () => {
    return (
        <PageWelcomeModal
            title="Welcome to Client Portal"
            description="Your complete project management and oversight dashboard."
            onStartTour={startClientPortalTour}
            tourId="client-portal-phase1"
        />
    );
};

export default ClientPortalTour;
